import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import DeliveryAddressForm, {
  EMPTY_DELIVERY_ADDRESS,
  mapGroupAddressToForm,
  toDeliveryAddressPayload,
  type DeliveryAddressForm as DeliveryAddressFormData,
  type DeliveryAddressFormHandle,
  type DeliveryOption,
  type GroupDeliveryAddress,
} from './DeliveryAddressForm';
import {
  applyRecommendedToClientAddress,
  formatSendcloudInvalidMessage,
  formatSendcloudStreet,
  isSendcloudAddressValid,
  sendcloudInvalidClientFields,
  pickRecommendedSendcloudAddress,
  recommendedChangesClientForm,
  validateClientAddress,
  type SendcloudRawAddress,
} from '../services/address-validate.service';
import { Truck } from 'lucide-react';

type CheckStatus = 'idle' | 'checking' | 'verified' | 'invalid' | 'error' | 'suggest';

const DELIVERY_LABELS: Record<DeliveryOption, string> = {
  individual: 'Envío individual',
  hybrid: 'Envío mixto',
  grouped: 'Envío grupal',
};

const ADDRESS_FIELDS: Array<keyof DeliveryAddressFormData> = [
  'recepient_name',
  'recepient_email',
  'recepient_phone',
  'recepient_line_1',
  'recepient_line_2',
  'recepient_postal_code',
  'recepient_city',
  'recepient_province',
  'recepient_country',
];

function isAddressEmpty(form: DeliveryAddressFormData): boolean {
  return ADDRESS_FIELDS.filter((key) => key !== 'recepient_line_2' && key !== 'recepient_country')
    .every((key) => !String(form[key] ?? '').trim());
}

function addressChanged(previous: DeliveryAddressFormData, next: DeliveryAddressFormData): boolean {
  return ADDRESS_FIELDS.some(
    (key) => String(previous[key] ?? '').trim() !== String(next[key] ?? '').trim(),
  );
}

interface OrganizerGroupAddressProps {
  groupId: string;
  groupName?: string;
  deliveryOption?: DeliveryOption;
  initialAddress?: GroupDeliveryAddress;
  /** Inline row for nesting inside another card or a status bar */
  embedded?: boolean;
}

export default function OrganizerGroupAddress({
  groupId,
  groupName,
  deliveryOption,
  initialAddress,
  embedded = false,
}: OrganizerGroupAddressProps) {
  const formRef = useRef<DeliveryAddressFormHandle>(null);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddressFormData>(
    EMPTY_DELIVERY_ADDRESS,
  );
  const [savedAddress, setSavedAddress] = useState<DeliveryAddressFormData>(EMPTY_DELIVERY_ADDRESS);
  const [existingId, setExistingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [checkStatus, setCheckStatus] = useState<CheckStatus>('idle');
  const [recommended, setRecommended] = useState<SendcloudRawAddress | null>(null);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [invalidFields, setInvalidFields] = useState<Array<keyof DeliveryAddressFormData>>([]);

  console.log('initialAddress', initialAddress);

  useEffect(() => {
    if (initialAddress?.id || initialAddress?.recipient_line_1 || initialAddress?.recepient_line_1) {
      const mapped = mapGroupAddressToForm(initialAddress);
      if (initialAddress.id) setExistingId(initialAddress.id);
      setDeliveryAddress(mapped);
      setSavedAddress(mapped);
    }
  }, [initialAddress]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase
        .from('delivery_addresses')
        .select('*')
        .eq('group_id', groupId)
        .eq('owner_type', 'group')
        .limit(1);
      if (cancelled) return;
      const row = (!error ? data?.[0] : null) as GroupDeliveryAddress | null;
      if (row?.id) {
        const mapped = mapGroupAddressToForm(row);
        setExistingId(row.id);
        setDeliveryAddress(mapped);
        setSavedAddress(mapped);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  useEffect(() => {
    if (open) return;
    setConfirming(false);
    setCheckStatus('idle');
    setRecommended(null);
    setDeliveryAddress(savedAddress);
  }, [open, savedAddress]);

  const persist = async (addressToSave = deliveryAddress) => {
    setSaving(true);
    const toastId = toast.loading('Guardando dirección...');
    try {
      if (!isAddressEmpty(addressToSave)) {
        const payload = {
          group_id: groupId,
          owner_type: 'group',
          ...toDeliveryAddressPayload(addressToSave, 'group'),
        };

        const query = existingId
          ? supabase.from('delivery_addresses').update(payload).eq('id', existingId).select('id').single()
          : supabase.from('delivery_addresses').insert(payload).select('id').single();

        const { data, error } = await query;
        if (error) throw error;
        setExistingId(data.id as string);
        setSavedAddress(addressToSave);
      }

      toast.success('Dirección guardada', { id: toastId });
      setConfirming(false);
      setOpen(false);
    } catch {
      toast.error('No se pudo guardar la dirección', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const continueAfterVerified = (next: DeliveryAddressFormData) => {
    setCheckStatus('verified');
    if (!isAddressEmpty(savedAddress) && addressChanged(savedAddress, next)) {
      setConfirming(true);
      return;
    }
    void persist(next);
  };

  const handleSave = async () => {
    if (isAddressEmpty(deliveryAddress)) {
      void persist();
      return;
    }

    if (!formRef.current?.validate()) {
      toast.error('Completa la dirección de entrega');
      return;
    }

    const alreadySawSuggest = checkStatus === 'suggest';
    setCheckStatus('checking');
    try {
      const result = await validateClientAddress(deliveryAddress);
      const suggestion = pickRecommendedSendcloudAddress(result);
      if (suggestion && recommendedChangesClientForm(deliveryAddress, suggestion) && !alreadySawSuggest) {
        setRecommended(suggestion);
        setCheckStatus('suggest');
        return;
      }
      if (isSendcloudAddressValid(result)) {
        setRecommended(null);
        continueAfterVerified(deliveryAddress);
        return;
      }
      setRecommended(null);
      setInvalidMessage(formatSendcloudInvalidMessage(result));
      setInvalidFields(sendcloudInvalidClientFields(result));
      setCheckStatus('invalid');
    } catch {
      setCheckStatus('error');
      toast.error('No se pudo comprobar la dirección');
    }
  };

  const handleUseRecommended = () => {
    if (!recommended) return;
    setDeliveryAddress(applyRecommendedToClientAddress(deliveryAddress, recommended));
    setRecommended(null);
    setCheckStatus('idle');
  };

  const canEditAddress = deliveryOption === 'grouped' || deliveryOption === 'hybrid';
  const addressSummary = isAddressEmpty(savedAddress)
    ? 'Dirección pendiente'
    : [savedAddress.recepient_line_1, savedAddress.recepient_city].filter(Boolean).join(', ');
  const deliveryLabel = DELIVERY_LABELS[deliveryOption ?? 'individual'];

  return (
    <>
      <button
        type="button"
        onClick={() => canEditAddress && setOpen(true)}
        disabled={!canEditAddress}
        className={
          embedded
            ? `min-w-0 flex-1 text-left rounded-md px-1 py-0.5 transition-colors ${
                canEditAddress ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
              }`
            : `bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-4 border-[#F90000] overflow-hidden min-w-0 w-full text-left transition-all ${
                canEditAddress ? 'hover:bg-gray-50 hover:shadow-md cursor-pointer' : 'cursor-default'
              }`
        }
      >
        {embedded ? (
          <div className="flex items-center gap-2 min-w-0">
            <Truck className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <p className="text-sm text-gray-800 truncate min-w-0">
              <span className="font-semibold">{deliveryLabel}</span>
              {canEditAddress && (
                <span className="text-gray-500 font-normal"> · {addressSummary ?? 'Dirección pendiente'}</span>
              )}
            </p>
            {canEditAddress && (
              <span className="inline-flex items-center gap-1 text-[#F90000] text-xs font-medium flex-shrink-0">
                Editar
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2 min-w-0">
            <div className="min-w-0">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-500">Envío</h3>
              <p className="text-sm sm:text-lg font-bold text-gray-800 truncate flex items-center gap-2">
                <Truck className="w-4 h-4" />
                {deliveryLabel}
              </p>
              {canEditAddress && (
                <p className="text-xs text-gray-500 truncate mt-0.5">{addressSummary}</p>
              )}
            </div>
            {canEditAddress && (
              <span className="inline-flex items-center gap-1 text-[#F90000] text-xs font-medium flex-shrink-0 mt-0.5">
                Editar
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            )}
          </div>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[99999] overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-full p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              aria-hidden="true"
              onClick={() => !saving && setOpen(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b">
                <h2 className="text-base font-semibold text-gray-800">
                  {confirming ? 'Confirmar cambio de dirección' : 'Dirección de entrega'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {confirming
                    ? 'Vas a modificar una dirección ya guardada.'
                    : groupName
                      ? `Dirección opcional para ${groupName}`
                      : 'Dirección opcional del grupo'}
                </p>
              </div>

              <div className="px-6 py-5 overflow-y-auto space-y-4">
                {confirming && (
                  <p className="text-sm text-gray-600">
                    Esta acción actualiza la dirección de entrega del grupo. Confirma para guardar el cambio.
                  </p>
                )}
                <div className={confirming ? 'hidden' : 'border rounded-md'}>
                  <div className="bg-gray-50 p-2.5 border-b">
                    <span className="font-medium text-gray-800">Dirección de entrega</span>
                    <p className="text-sm text-gray-500 mt-1">Opcional.</p>
                  </div>
                  <div className="p-4">
                    <DeliveryAddressForm
                      ref={formRef}
                      deliveryAddress={deliveryAddress}
                      setDeliveryAddress={setDeliveryAddress}
                      highlightFields={invalidFields}
                      onClearHighlight={(field) =>
                        setInvalidFields((prev) => prev.filter((item) => item !== field))
                      }
                    />
                  </div>
                </div>
                {checkStatus === 'checking' && (
                  <p className="text-sm text-gray-600">Comprobando la dirección…</p>
                )}
                {checkStatus === 'verified' && (
                  <p className="text-sm text-green-700">Dirección verificada</p>
                )}
                {checkStatus === 'error' && (
                  <p className="text-sm text-red-600">No se pudo comprobar la dirección</p>
                )}
                {checkStatus === 'invalid' && (
                  <p className="text-sm text-amber-900">{invalidMessage}</p>
                )}
                {checkStatus === 'suggest' && recommended && (
                  <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <p className="text-sm font-medium text-amber-900">Dirección sugerida</p>
                    <p className="text-sm text-gray-700">{formatSendcloudStreet(recommended)}</p>
                    <button
                      type="button"
                      onClick={handleUseRecommended}
                      disabled={saving}
                      className="px-3 py-1.5 rounded-lg border border-amber-300 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      Usar esta dirección
                    </button>
                    <p className="text-xs text-gray-500">O pulsa Guardar para mantener la tuya si es válida.</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2">
                {confirming ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Volver
                    </button>
                    <button
                      type="button"
                      onClick={() => void persist(deliveryAddress)}
                      disabled={saving}
                      className="px-4 py-2 rounded-lg bg-[#F90000] hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {saving ? 'Guardando...' : 'Confirmar'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      disabled={saving || checkStatus === 'checking'}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleSave()}
                      disabled={saving || checkStatus === 'checking'}
                      className="px-4 py-2 rounded-lg bg-[#F90000] hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {checkStatus === 'checking' ? 'Comprobando…' : saving ? 'Guardando...' : 'Guardar'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
